import Big from 'big.js';
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from 'mobx';
import { Path } from 'react-hook-form';
import { ErrorContextToFieldConverter } from '@alfa-retail-react/components';

import { LoadStateStore } from 'app/loading/LoadStateStore';
import { Employment } from 'app/model/Employment';
import { ProductService } from 'app/service/ProductService';
import { CardProduct } from 'app/model/product/CardProduct';
import { MapBuilder } from 'app/utils/MapBuilder';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/FeatureFlags';
import { ProductParametersConfigureFormValues } from 'app/logic/pages/productParametersConfigure/ProductParametersConfigurePage';
import { ProductParametersConfigureFormStore } from 'app/logic/pages/productParametersConfigure/ProductParametersConfigureFormStore';
import { OrderService } from 'app/service/OrderService';
import { CardOrder } from 'app/model/CardOrder';
import { ResourceFormat } from 'app/model/ResourceFormat';
import { FileInformation } from 'app/utils/FilesUtils';
import { RestoreFormData } from 'app/types/store';

export class ProductParametersConfigurePageStore {
    private readonly productService: ProductService = new ProductService();

    private readonly orderService: OrderService = new OrderService();

    readonly loadStateStore: LoadStateStore = new LoadStateStore();

    readonly actionStateStore: LoadStateStore = new LoadStateStore(true);

    @observable
    formStore: ProductParametersConfigureFormStore;

    @observable
    private _cardProduct: CardProduct;

    @observable
    private _order: CardOrder;

    readonly contextConverter =
        new ErrorContextToFieldConverter<ProductParametersConfigureFormValues>(
            MapBuilder.create<
                string,
                Array<Path<ProductParametersConfigureFormValues>>
            >()
                .value('insuranceProductCode', ['insurance'])
                .value('amount', ['limit'])
                .value('currency', ['limit'])
                .defaultValue([])
                .build(),
        );

    constructor() {
        makeObservable(this);
    }

    @action
    async initialize(orderId: string, isInitialCreation: boolean) {
        return this.loadStateStore.loadData(async () => {
            const order = await this.orderService.getById(orderId);
            const cardProduct = await this.productService.getCardProduct(
                order.cardProductCode,
                order.saleChannel,
            );
            const { product, creditLimit } = this.getProductByActivity(
                order,
                cardProduct,
            );

            runInAction(() => {
                this.setOrder(order);
                this.setCardProduct(product);
            });

            if (isInitialCreation) {
                this.formStore = ProductParametersConfigureFormStore.default(
                    product,
                    creditLimit,
                );
            } else {
                this.formStore = ProductParametersConfigureFormStore.fromOrder(
                    product,
                    order,
                );
            }
        });
    }

    getProductByActivity(order: CardOrder, cardProduct: CardProduct) {
        const activityKindCode = order.client.employment?.activityKindCode;
        const isSoleTrader = Employment.isActivityHasUNP(activityKindCode);
        const productMaxAmount = ProductParametersConfigureFormStore.getMaxAmount({ cardProduct, activityKindCode });
    
        const creditLimit = order.amount ?? productMaxAmount;
        const product = isSoleTrader
            ? CardProduct.deserialize({
                  ...cardProduct,
                  maxAmount: productMaxAmount,
              })
            : cardProduct;

        return { product, creditLimit: String(creditLimit) };
    }

    /**
     * Возвращает данные из заполненной формы текущего шага
     */
    @computed
    get restorationFormData(): RestoreFormData<ProductParametersConfigureFormStore> {
        return { ...this.formStore, insuranceAppliedState: this.formStore.insuranceAppliedState };
    }

    /**
     * Заполняет форму данными, сохраненными ранее при заполнении
     */
    @action
    restoreFormData(formData: RestoreFormData<ProductParametersConfigureFormStore>) {
        runInAction(() => {
            const maxAmount = this.cardProduct.maxAmount.toNumber();

            /**
             * После смены места работы с наемного рабочего на ИП лимит может уменьшиться, делая выбранный лимит невалидным
             * В таком случае выбранная пользователем страховка тоже может оказаться не валидной
            */

            if (+formData.creditLimit < maxAmount) {
                this.formStore.setCreditLimit(formData.creditLimit);
            }

            if (isFeatureEnabled(FeatureFlag.INSURANCE_NEW_VIEW)) {
                return;
            }

            if (formData.insuranceProduct && +this.formStore.creditLimit >= formData.insuranceProduct.minCreditAmount) {
                this.formStore.setInsurance(formData.insuranceProduct);
                this.formStore.setHasInsurance(formData.insuranceAppliedState);
            }
        });
    }

    @computed
    get hasInsurance(): boolean {
        return this.formStore?.insuranceStore.hasInsurance;
    }

    @computed
    get cardProduct(): CardProduct {
        return this._cardProduct;
    }

    @computed
    get order() {
        return this._order;
    }

    @action
    setCardProduct(cardProduct: CardProduct) {
        this._cardProduct = cardProduct;
    }

    @action
    setOrder(order: CardOrder) {
        this._order = order;
    }

    downloadInsuranceInstructions = (
        productCode: string,
        documentCode: string,
        format: ResourceFormat,
    ): Promise<FileInformation> => {
        return this.actionStateStore.loadData(() => {
            return this.productService.getInsuranceInstructions(
                productCode,
                documentCode,
                format,
            );
        });
    };
}
